import { awaitRedraw, el } from "@dhx/ts-common/dom";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import { IEditor, SpreadsheetEvents } from "../types";
import { IEventSystem } from "@dhx/ts-common/events";
import { List, ListEvents } from "@dhx/ts-list";
import { DataCollection } from "@dhx/ts-data";
import { focusManager } from "@dhx/ts-common/FocusManager";
import { isNumeric } from "../helpers/format";

export class SelectEditor implements IEditor {
	protected _handlers: { [key: string]: (...args) => void };
	protected _config: any;
	protected _input: HTMLInputElement;
	private _list: List;
	private _data: DataCollection;
	private _popup: Popup;
	private _events: IEventSystem<SpreadsheetEvents>;
	private _endEdit: (withoutSave?: boolean) => void;
	private edit: boolean;

	constructor(cb, events) {
		this._list = new List(null, { keyNavigation: true, css: "dhx_editor-list" });
		this._data = this._list.data;

		this._popup = new Popup({ css: "dhx_editor-popup" });
		this._popup.attach(this._list);
		this._events = events;
		this._endEdit = cb;
		this._initHandlers();
	}

	endEdit() {
		if (!this.edit) return;
		awaitRedraw().then(() => {
			this._events.fire(SpreadsheetEvents.cellInput, [
				this._config.oninput[1],
				this._list.selection.getItem()?.value || this._input.value,
			]);
			this._endEdit();
			this._popup.hide();
		});
		this.edit = false;
	}

	setValue(data: any) {
		const values = data.filter(i => i || isNumeric(i)).map(i => i?.toString().trim() || "");
		const set = new Set(values);
		this._data.parse(Array.from(set).map(i => ({ value: i })));
	}

	toHTML(conf: any) {
		this._config = conf;
		this.edit = true;

		document.addEventListener("mousedown", this._handlers.onOuterClick);
		return [
			el("input.dhx_cell_input", {
				...conf,
				onkeydown: e => {
					if (e.code === "Enter") {
						this._popup.hide();
					}
					if (e.code === "ArrowDown" || e.code === "ArrowUp") {
						focusManager.setFocusId((this._list as any)._uid);
					}
				},
				oninput: this._handlers.oninput,
				_hooks: {
					...conf._hooks,
					didInsert: this._handlers.didInsert,
					didRemove: () => document.removeEventListener("mousedown", this._handlers.onOuterClick),
				},
			}),
			el(".dxi.dxi-menu-up.dhx_spreadsheet_cell_icon", {
				onclick: () => this.endEdit(),
			}),
		];
	}

	private _filter(val: string) {
		this._list.data.filter(item => (val ? item.value.includes(val) : item));
	}

	protected _showPopup() {
		this._popup.show(this._input, {
			centering: true,
			mode: "bottom",
		});
		this._list.selection.add(this._list.data.find(i => i.value === this._input.value)?.id || "");
	}

	protected _initHandlers() {
		this._handlers = {
			onFocus: () => {
				awaitRedraw().then(() => {
					this._showPopup();
				});
			},
			onOuterClick: (e: Event) => {
				if (e.target instanceof Node) {
					const isInput = this._input && this._input.contains(e.target);
					const isPopup =
						this._popup &&
						this._popup.getRootNode() &&
						this._popup.getRootNode().contains(e.target);
					if (!(isInput || isPopup)) {
						this._popup.hide();
					}
				}
			},
			oninput: (e: any) => {
				const value = e.target.value;
				if (!value) {
					this._showPopup();
				}
				if (value.startsWith("=") && this._popup.isVisible()) {
					this._popup.hide();
				}
				this._filter(value);
				const [cb, cell] = this._config.oninput;
				cb(cell, e);
			},
			didInsert: (node: any) => {
				const input: HTMLInputElement = node.el;
				this._input = input;
				this._input.addEventListener("focus", this._handlers.onFocus);
				this._config._hooks.didInsert(node);
			},
		};
		this._list.events.on(ListEvents.click, () => {
			this.endEdit();
		});

		this._events.on(SpreadsheetEvents.afterEditEnd, () => {
			this._popup.hide();
		});
		this._popup.events.on(PopupEvents.afterShow, () => {
			const focus = this._list.getFocus();
			if (!this._list.data.getItem(focus)) {
				this._list.setFocus(this._list.data.getId(0));
			}
		});
	}
}
